// src/pages/MyGrowthPage/MyGrowthPage.jsx
import React, { useState, useEffect } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { format, subDays, startOfDay, isSameDay } from "date-fns";
import Navbar from "../components/Navbar";
import StatsGrid from "../components/growthPageComponents/StatsGrid";
import LevelProgress from "../components/growthPageComponents/LevelProgress";
import ChartsSection from "../components//growthPageComponents/ChartsSection";
import WorkHoursAnalysis from "../components/growthPageComponents/WorkHoursAnalysis";
import StreakCalendar from "../components/growthPageComponents/StreakCalendar";
import InsightsGrid from "../components/growthPageComponents/InsightsGrid";
import Milestones from "../components/growthPageComponents/Milestones";

// Import calculations
import {
  calculateXP,
  calculateLevel,
  secondsToHours,
} from "../utils/calculations";

// =========================================================
// STYLED COMPONENTS
// =========================================================
const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
  }
`;

const PageContainer = styled.div`
  max-width: 1700px;
  margin: 0 auto;
  padding: 20px;
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 40px;
  padding: 40px 20px 20px;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  background: linear-gradient(90deg, var(--primaryColor), #8b5cf6);
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  color: var(--fontSecondaryColor);
  font-size: 1rem;
  margin: 0;
  padding: 0;
`;

// =========================================================
// MAIN COMPONENT
// =========================================================

const MyGrowthPage = () => {
  const { currentUser } = useAuth();
  const userId = currentUser?.uid;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    document.title = "My Growth - ASites Planner";
  }, []);

  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("7d");
  const [growthData, setGrowthData] = useState(null);

  useEffect(() => {
    if (!userId) return;

    const fetchGrowthData = async () => {
      setLoading(true);
      try {
        const today = new Date();
        const daysToFetch =
          timeRange === "7d" ? 7 : timeRange === "30d" ? 30 : 90;
        const startDate = subDays(today, daysToFetch);

        const [
          taskProgressSnap,
          habitProgressSnap,
          tasksSnap,
          habitsSnap,
          workHoursSnap,
        ] = await Promise.all([
          getDocs(
            query(
              collection(db, "users", userId, "dashboardProgress"),
              where("date", ">=", startOfDay(startDate)),
              orderBy("date", "asc")
            )
          ),
          getDocs(
            query(
              collection(db, "users", userId, "habitProgress"),
              where("date", ">=", startOfDay(startDate)),
              orderBy("date", "asc")
            )
          ),
          getDocs(collection(db, "users", userId, "dashboardTasks")),
          getDocs(collection(db, "users", userId, "habits")),
          getDocs(
            query(
              collection(db, "users", userId, "workHours"),
              where("updatedAt", ">=", startDate),
              orderBy("updatedAt", "asc")
            )
          ),
        ]);

        const taskProgressData = taskProgressSnap.docs.map((doc) => ({
          id: doc.id,
          date: doc.data().date?.toDate() || new Date(),
          percentage: doc.data().percentage || 0,
        }));

        const habitProgressData = habitProgressSnap.docs.map((doc) => ({
          id: doc.id,
          date: doc.data().date?.toDate() || new Date(),
          percentage: doc.data().percentage || 0,
        }));

        const workHoursData = workHoursSnap.docs
          .map((doc) => ({
            date: doc.data().updatedAt?.toDate() || new Date(),
            seconds: doc.data().seconds || 0,
            dateKey: doc.id,
          }))
          .sort((a, b) => a.date - b.date);

        // Create a map of all dates in the range
        const allDatesMap = new Map();
        for (let i = 0; i <= daysToFetch; i++) {
          const date = subDays(today, i);
          const dateKey = format(date, "yyyy-MM-dd");
          allDatesMap.set(dateKey, {
            date: startOfDay(date),
            taskScore: 0,
            habitScore: 0,
            hasData: false,
          });
        }

        // Fill in task scores
        taskProgressData.forEach((item) => {
          const dateKey = format(item.date, "yyyy-MM-dd");
          if (allDatesMap.has(dateKey)) {
            allDatesMap.get(dateKey).taskScore = item.percentage;
            allDatesMap.get(dateKey).hasData = true;
          }
        });

        // Fill in habit scores
        habitProgressData.forEach((item) => {
          const dateKey = format(item.date, "yyyy-MM-dd");
          if (allDatesMap.has(dateKey)) {
            allDatesMap.get(dateKey).habitScore = item.percentage;
            allDatesMap.get(dateKey).hasData = true;
          }
        });

        // Calculate totals
        let totalTasksCompleted = 0;
        let totalHabitsCompleted = 0;

        const allTasks = tasksSnap.docs.map((doc) => doc.data());
        const allHabits = habitsSnap.docs.map((doc) => doc.data());

        allTasks.forEach((task) => {
          if (task.completedDates) {
            totalTasksCompleted += Object.values(task.completedDates).filter(
              Boolean
            ).length;
          }
        });

        allHabits.forEach((habit) => {
          if (habit.completedDates) {
            totalHabitsCompleted += Object.values(habit.completedDates).filter(
              Boolean
            ).length;
          }
        });

        // Process daily scores
        const dailyScores = [];
        let currentStreak = 0;
        let maxStreak = 0;
        let totalPerfectDays = 0;

        const sortedDates = Array.from(allDatesMap.values()).sort(
          (a, b) => a.date - b.date
        );

        sortedDates.forEach((day) => {
          const combinedScore = day.hasData
            ? Math.round((day.taskScore + day.habitScore) / 2)
            : 0;

          const isPerfectDay = combinedScore >= 100;
          const isGoodDay = combinedScore >= 80;

          if (isPerfectDay) {
            currentStreak++;
            totalPerfectDays++;
          } else {
            currentStreak = 0;
          }

          if (currentStreak > maxStreak) {
            maxStreak = currentStreak;
          }

          dailyScores.push({
            date: format(day.date, "MMM d"),
            fullDate: day.date,
            score: combinedScore,
            taskScore: day.taskScore,
            habitScore: day.habitScore,
            isPerfect: isPerfectDay,
            isGood: isGoodDay,
            hasData: day.hasData,
          });
        });

        // Calculate growth score
        const last7Days = dailyScores.slice(-7).filter((d) => d.hasData);
        const growthScore =
          last7Days.length > 0
            ? Math.round(
                last7Days.reduce((a, b) => a + b.score, 0) / last7Days.length
              )
            : 0;

        // Calculate productivity velocity
        let productivityVelocity = 0;
        if (dailyScores.length >= 14) {
          const recentScores = dailyScores.slice(-7).filter((d) => d.hasData);
          const previousScores = dailyScores
            .slice(-14, -7)
            .filter((d) => d.hasData);

          if (recentScores.length > 0 && previousScores.length > 0) {
            const recentAvg =
              recentScores.reduce((a, b) => a + b.score, 0) /
              recentScores.length;
            const previousAvg =
              previousScores.reduce((a, b) => a + b.score, 0) /
              previousScores.length;

            if (previousAvg > 0) {
              productivityVelocity = Math.round(
                ((recentAvg - previousAvg) / previousAvg) * 100
              );
            }
          }
        }

        // Calculate average score
        const daysWithData = dailyScores.filter((d) => d.hasData);
        const averageScore =
          daysWithData.length > 0
            ? Math.round(
                daysWithData.reduce((sum, day) => sum + day.score, 0) /
                  daysWithData.length
              )
            : 0;

        const consistentDays = dailyScores.filter((d) => d.score >= 80).length;
        const consistency =
          dailyScores.length > 0
            ? Math.round((consistentDays / dailyScores.length) * 100)
            : 0;

        const bestDay =
          daysWithData.length > 0
            ? daysWithData.reduce((best, current) =>
                current.score > best.score ? current : best
              )
            : null;

        // Calculate XP and level
        const totalXP = calculateXP(
          totalTasksCompleted,
          totalHabitsCompleted,
          totalPerfectDays
        );
        const currentLevel = calculateLevel(totalXP);

        // Prepare data for components
        const data = {
          // Stats
          growthScore,
          productivityVelocity,
          perfectDayStreak: maxStreak,
          currentLevel,

          // Progress data
          dailyProgress: dailyScores,
          taskCompletion: dailyScores.map((day) => ({
            date: day.date,
            value: day.taskScore,
          })),
          habitCompletion: dailyScores.map((day) => ({
            date: day.date,
            value: day.habitScore,
          })),

          // Insights
          bestDay,
          averageScore,
          consistency,
          totalTasksCompleted,
          totalHabitsCompleted,
          totalPerfectDays,

          // Work hours
          workHoursData,
          workHoursStats: {
            totalHours: secondsToHours(
              workHoursData.reduce((sum, day) => sum + day.seconds, 0)
            ),
            averageDailyHours:
              workHoursData.length > 0
                ? secondsToHours(
                    workHoursData.reduce((sum, day) => sum + day.seconds, 0)
                  ) / workHoursData.length
                : 0,
            workDaysCount: workHoursData.filter(
              (day) => secondsToHours(day.seconds) > 0.5
            ).length,
          },

          // Time range
          timeRange,
        };

        setGrowthData(data);
      } catch (error) {
        console.error("Error fetching growth data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchGrowthData();
  }, [userId, timeRange]);

  if (loading) {
    return (
      <PageContainer>
        <div style={{ textAlign: "center", padding: "100px 20px" }}>
          <div style={{ fontSize: "2rem", marginBottom: "20px" }}>📈</div>
          <h2>Loading Growth Analysis...</h2>
          <p>Crunching your productivity numbers...</p>
        </div>
      </PageContainer>
    );
  }

  if (!growthData) {
    return (
      <PageContainer>
        <div style={{ textAlign: "center", padding: "100px 20px" }}>
          <div style={{ fontSize: "2rem", marginBottom: "20px" }}>📊</div>
          <h2>No Data Available</h2>
          <p>Start completing tasks and habits to see your growth metrics!</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <>
      <GlobalStyle />
      <Navbar />

      <PageContainer>
        <Header>
          <Title>My Growth</Title>
          <Subtitle>Track your evolution. Measure your mastery.</Subtitle>

          <div
            style={{
              marginTop: "20px",
              display: "flex",
              gap: "10px",
              justifyContent: "center",
              color: "var(--fontPrimaryColor)",
            }}
          >
            {["7d", "30d", "90d"].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                style={{
                  padding: "8px 20px",
                  borderRadius: "20px",
                  border: "none",
                  background:
                    timeRange === range
                      ? "var(--secondaryBackgroundColor)"
                      : "var(--thirdBackgroundColor)",
                  color:
                    timeRange === range
                      ? "var(--primaryColor)"
                      : "var(--fontSecondaryColor)",
                  cursor: "pointer",
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: "500",
                  transition: "all 0.5s ease",
                }}
              >
                {range === "7d"
                  ? "Last 7 Days"
                  : range === "30d"
                  ? "Last 30 Days"
                  : "Last 90 Days"}
              </button>
            ))}
          </div>
        </Header>

        {/* Stats Grid */}
        <StatsGrid
          growthScore={growthData.growthScore}
          productivityVelocity={growthData.productivityVelocity}
          perfectDayStreak={growthData.perfectDayStreak}
          currentLevel={growthData.currentLevel}
        />

        {/* Level Progress */}
        <LevelProgress
          currentLevel={growthData.currentLevel}
          totalTasksCompleted={growthData.totalTasksCompleted}
          totalHabitsCompleted={growthData.totalHabitsCompleted}
          totalPerfectDays={growthData.totalPerfectDays}
        />

        {/* Charts Section */}
        <ChartsSection
          dailyProgress={growthData.dailyProgress}
          taskCompletion={growthData.taskCompletion}
          habitCompletion={growthData.habitCompletion}
        />

        {/* Work Hours Analysis */}
        <WorkHoursAnalysis
          workHoursData={growthData.workHoursData}
          dailyProgress={growthData.dailyProgress}
          workHoursStats={growthData.workHoursStats}
          timeRange={growthData.timeRange}
        />

        {/* Streak Calendar */}
        <StreakCalendar
          dailyProgress={growthData.dailyProgress}
          perfectDayStreak={growthData.perfectDayStreak}
        />

        {/* Insights Grid */}
        <InsightsGrid
          bestDay={growthData.bestDay}
          consistency={growthData.consistency}
          totalTasksCompleted={growthData.totalTasksCompleted}
          totalHabitsCompleted={growthData.totalHabitsCompleted}
          totalPerfectDays={growthData.totalPerfectDays}
          productivityVelocity={growthData.productivityVelocity}
          averageScore={growthData.averageScore}
        />

        {/* Milestones */}
        <Milestones
          currentLevel={growthData.currentLevel}
          perfectDayStreak={growthData.perfectDayStreak}
          totalTasksCompleted={growthData.totalTasksCompleted}
          totalHabitsCompleted={growthData.totalHabitsCompleted}
        />
      </PageContainer>
    </>
  );
};

export default MyGrowthPage;
